import { useEffect, useState } from "react";
import MfaSetupPopup from "../popups/mfaSetupPopup";
import { useMfaEnrollmentState } from "../../services/utils/custom hooks/Mfa/useMfaFactors";
import { IS_MFA_SOFT_LAUNCH_ENABLED } from "../../constants/mfaFlags";
import {
  dismissMfaSoftPrompt,
  isMfaGracePeriodActive,
  isMfaSoftPromptDismissed,
} from "../../services/utils/helpers/mfaHelpers";

interface MfaPromptGateProps {
  termsAccepted: boolean;
}

function MfaPromptGate({
  termsAccepted,
}: Readonly<MfaPromptGateProps>): JSX.Element | null {
  const { enrolled, isLoading, isFetching, isError } = useMfaEnrollmentState();
  const [softDismissed, setSoftDismissed] = useState(isMfaSoftPromptDismissed);
  const [showSetup, setShowSetup] = useState(false);
  const [finishedThisSession, setFinishedThisSession] = useState(false);

  const graceActive = isMfaGracePeriodActive();
  const mfaReady =
    IS_MFA_SOFT_LAUNCH_ENABLED &&
    termsAccepted &&
    !isLoading &&
    !isFetching &&
    !isError;

  useEffect(() => {
    setSoftDismissed(isMfaSoftPromptDismissed());
  }, [enrolled]);

  useEffect(() => {
    if (!mfaReady || finishedThisSession || enrolled) {
      setShowSetup(false);
      return;
    }
    if (!graceActive || !softDismissed) {
      setShowSetup(true);
      return;
    }
    setShowSetup(false);
  }, [
    mfaReady,
    enrolled,
    graceActive,
    softDismissed,
    finishedThisSession,
  ]);

  if (!IS_MFA_SOFT_LAUNCH_ENABLED || !termsAccepted || !showSetup) {
    return null;
  }

  function handleSkip() {
    if (!graceActive) return;
    dismissMfaSoftPrompt();
    setSoftDismissed(true);
    setShowSetup(false);
  }

  function handleClose() {
    if (graceActive) {
      handleSkip();
      return;
    }
    // Required: only close after enroll (parent hides via enrolled)
  }

  function handleEnrolled() {
    setFinishedThisSession(true);
    setShowSetup(false);
  }

  return (
    <MfaSetupPopup
      onClose={handleClose}
      onEnrolled={handleEnrolled}
      allowSkip={graceActive}
      required={!graceActive}
    />
  );
}

export default MfaPromptGate;
